
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	CompanyUserRolePermission,
	CompanyUserRolePermissionGroup,
	COMPANY_USER_ROLE_PERMISSION_DATA,
	COMPANY_USER_ROLE_PERMISSION_GROUP,
	CompanyUserRoleDef,
	COMPANY_USER_ROLE_PERMISSION_GROUP_LABEL,
	Team,
	User,
} from '@run-diffusion/shared';
import _mapKeys from 'lodash/mapKeys';
import _cloneDeep from 'lodash/cloneDeep';
import EmptyState from '@/components/states/EmptyState.vue';
import { ROUTER } from '@/router/constants';
import BackButton from '@/components/base/BackButton.vue';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import { db, functions } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import BaseButton from '@/components/base/BaseButton.vue';
import ColorInput from '@/components/ColorInput.vue';
import RoleDefBadge from '@/views/Team/CompanyDashboard/RoleDefBadge.vue';
import { TeamUserMixin } from '@/mixins';
import { COMPANY_DIRECTORY_TITLE } from '@/views/Team/CompanyNav/companyNavState';
import BaseToggle from '@/components/base/BaseToggle.vue';

export default Vue.extend({
	name: 'ManageCompanyRolesPage',
	metaInfo () {
		return {
			title: `Company Roles | ${this.team?.company?.name || 'Company'} | RunDiffusion`,
		};
	},
	data () {
		return {
			COMPANY_USER_ROLE_PERMISSION_GROUP_LABEL,

			boundProps: {
				userId: null,
				companyId: null,
			},
			availableRoleDefs: [] as CompanyUserRoleDef[],
			availableRoleDefsMap: {} as Record<string, CompanyUserRoleDef>,
			loadingAvailableRoleDefs: false,

			selectedRoleDef: null as CompanyUserRoleDef | null,
			originalRoleDef: null as CompanyUserRoleDef | null, // the role to reset to
			editingRoleDef: null as CompanyUserRoleDef | null, // to role that is being edited
			isEditMode: false,

			saving: false,
		};
	},
	mixins: [
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// Company roles are assigned from the Directory; this page only defines them.
		directoryTitle (): string {
			return COMPANY_DIRECTORY_TITLE;
		},
		membersDirectoryRoute () {
			return { name: ROUTER.MEMBERS_DIRECTORY };
		},
		breadcrumbs () {
			return [
				{
					hoverColor: 'var(--primary-purple)',
					text: 'Company Dashboard',
					disabled: false,
					to: {
						name: ROUTER.COMPANY_DASHBOARD,
					},
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: 'Company Settings',
					disabled: false,
					to: {
						name: ROUTER.COMPANY_SETTINGS,
					},
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: 'Company Roles',
					disabled: true,
					to: {
						name: ROUTER.COMPANY_ROLES,
					},
				},
			];
		},
		permissionTypes (): CompanyUserRolePermissionGroup[] {
			return Object.values(COMPANY_USER_ROLE_PERMISSION_GROUP).filter(group => [COMPANY_USER_ROLE_PERMISSION_GROUP.COMPANY].includes(group)) as CompanyUserRolePermissionGroup[];
		},
		filteredPermissionTypes () {
			return this.permissionTypes.filter(group => (this.permissionsByGroup[group] || []).length > 0);
		},
		permissionsByGroup () {
			return Object.keys(COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)).reduce((acc, key: CompanyUserRolePermission) => {
				const permission = COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)[key];
				acc[permission.group] = acc[permission.group] || [];
				acc[permission.group].push(key);
				return acc;
			}, {} as Record<CompanyUserRolePermissionGroup, CompanyUserRolePermission[]>);
		},
		hasUnsavedChanges () {
			if (!this.editingRoleDef || !this.originalRoleDef) return false;

			// Check if any fields have changed
			return (
				this.editingRoleDef.name !== this.originalRoleDef.name ||
				this.editingRoleDef.description !== this.originalRoleDef.description ||
				this.editingRoleDef.color !== this.originalRoleDef.color ||
				this.editingRoleDef.required !== this.originalRoleDef.required ||
				JSON.stringify(this.editingRoleDef.permissions) !== JSON.stringify(this.originalRoleDef.permissions)
			);
		},
	},
	watch: {
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const newCompanyId: string = newVal?.companyId || null;
				const oldCompanyId: string = oldVal?.companyId || null;
				if (newCompanyId !== oldCompanyId) {
					this.boundProps = {
						...this.boundProps,
						companyId: newCompanyId,
					};
				}
			},
		},
		user: {
			immediate: true,
			handler (newVal: User, oldVal: User) {
				const newUserId: string = newVal?.id || null;
				const oldUserId: string = oldVal?.id || null;
				if (newUserId !== oldUserId) {
					this.boundProps = {
						...this.boundProps,
						userId: newUserId,
					};
				}
			},
		},
		boundProps: {
			immediate: true,
			deep: true,
			handler (newVal, oldVal) {
				if (newVal !== oldVal) {
					// Use boundProps object to ensure the binding is only called once for a set of dependent values
					this.bindCompanyUserRoleDefs(newVal.userId, newVal.companyId);
				}
			},
		},
		availableRoleDefs: {
			immediate: true,
			handler (newVal: CompanyUserRoleDef[]) {
				this.availableRoleDefsMap = _mapKeys(newVal, 'id');
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		COMPANY_USER_ROLE_PERMISSION_DATA,
		isPermissionDisabled (permissionKey: any) {
			if (!this.editingRoleDef) return true;
			const permissionMeta = this.COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)[permissionKey];
			const dependsOn = permissionMeta?.dependsOn || [];
			if (!dependsOn.length) return false;
			return dependsOn.some((depKey: any) => {
				return !this.editingRoleDef?.permissions?.[depKey];
			});
		},
		onPermissionChange (permissionKey: CompanyUserRolePermission, value: boolean) {
			if (!this.editingRoleDef) return;

			this.editingRoleDef = {
				...this.editingRoleDef,
				permissions: {
					...this.editingRoleDef.permissions,
					[permissionKey]: value,
				},
			} as CompanyUserRoleDef;

			// If permission is turned off, also disable permissions that depend on it.
			if (!value) {
				this.turnOffDependentPermissions(permissionKey);
			}
		},
		/**
		 * Safely turns off dependent permissions with protection against circular dependencies.
		 *
		 * Protection mechanisms:
		 * 1. Depth limit (max 10 levels) - prevents stack overflow
		 * 2. Processed set tracking - detects when we've already processed a permission
		 * 3. Console warnings - alerts developers to bad data
		 * 4. Graceful exit - continues processing other permissions
		 *
		 * Example of bad data that could cause infinite loops:
		 * Permission A depends on Permission B
		 * Permission B depends on Permission C
		 * Permission C depends on Permission A (CIRCULAR!)
		 */
		turnOffDependentPermissions (parentPermissionKey: CompanyUserRolePermission) {
			if (!this.editingRoleDef) return;

			const allPermissions = Object.keys(this.COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)) as CompanyUserRolePermission[];
			const permissionsToTurnOff: CompanyUserRolePermission[] = [];
			const processedPermissions = new Set<CompanyUserRolePermission>(); // Track processed permissions to prevent infinite loops

			// Find all permissions that depend on the parent permission
			allPermissions.forEach((permissionKey) => {
				const permissionData = this.COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)[permissionKey];
				const dependsOn = permissionData?.dependsOn || [];

				if (dependsOn.includes(parentPermissionKey)) {
					permissionsToTurnOff.push(permissionKey);
				}
			});

			// Recursively turn off dependent permissions and their dependencies
			const turnOffRecursively = (permissionKey: CompanyUserRolePermission, depth = 0) => {
				// Safety check: prevent infinite loops
				if (depth > 10) {
					console.warn(`[turnOffDependentPermissions] Maximum recursion depth reached for permission: ${permissionKey}. Possible circular dependency detected.`);
					return;
				}

				// Check if we've already processed this permission to prevent circular dependencies
				if (processedPermissions.has(permissionKey)) {
					console.warn(`[turnOffDependentPermissions] Circular dependency detected for permission: ${permissionKey}. Skipping to prevent infinite loop.`);
					return;
				}

				// Mark this permission as being processed
				processedPermissions.add(permissionKey);

				// Turn off this permission
				this.editingRoleDef = {
					...this.editingRoleDef,
					permissions: {
						...this.editingRoleDef.permissions,
						[permissionKey]: false,
					},
				};

				// Find and turn off permissions that depend on this one
				allPermissions.forEach((dependentKey) => {
					const dependentData = this.COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels)[dependentKey];
					const dependsOn = dependentData?.dependsOn || [];

					if (dependsOn.includes(permissionKey) && this.editingRoleDef?.permissions?.[dependentKey]) {
						turnOffRecursively(dependentKey, depth + 1);
					}
				});
			};

			// Turn off all dependent permissions recursively
			permissionsToTurnOff.forEach((permissionKey) => {
				// Reset processed set for each top-level permission to allow proper dependency chains
				processedPermissions.clear();
				turnOffRecursively(permissionKey);
			});
		},
		goBack () {
			// Go through breadcrumbs in reverse order to find the back route
			for (let i = this.breadcrumbs.length - 1; i >= 0; i--) {
				const breadcrumb = this.breadcrumbs[i];
				if (breadcrumb.to && !breadcrumb.disabled) {
					this.$router.push(breadcrumb.to);
					return;
				}
			}
		},
		async bindCompanyUserRoleDefs (userId: string, companyId: string) {
			if (!userId || !companyId) {
				this.availableRoleDefs = [];
				return;
			}

			this.loadingAvailableRoleDefs = true;
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const roleDefsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind(
					'availableRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);

			} catch (error) {
				console.error('Error binding company user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load company roles',
					show: true,
				});
			} finally {
				this.loadingAvailableRoleDefs = false;
			}
		},
		onSwitchRoleDef (roleDefId: string) {
			if (!roleDefId) return;

			const roleDef: CompanyUserRoleDef = this.availableRoleDefsMap[roleDefId] || null;
			if (!roleDef) return;

			this.originalRoleDef = _cloneDeep(roleDef);
			this.editingRoleDef = _cloneDeep(roleDef);
			this.initializeRolePermissionKeys();
			this.isEditMode = false; // Reset edit mode when switching roles
		},
		onClearRole () {
			this.originalRoleDef = null;
			this.editingRoleDef = null;
			this.isEditMode = false; // Reset edit mode when clearing selection
		},
		initializeRolePermissionKeys () {
			if (!this.editingRoleDef) return;
			if (!this.editingRoleDef.permissions) {
				this.$set(this.editingRoleDef as any, 'permissions', {});
			}
			const allKeys = Object.keys(this.COMPANY_USER_ROLE_PERMISSION_DATA(this.displayLabels));
			allKeys.forEach((key: string) => {
				if (typeof (this.editingRoleDef as any).permissions[key] === 'undefined') {
					this.$set((this.editingRoleDef as any).permissions, key, false);
				}
			});
		},
		enterEditMode () {
			if (!this.editingRoleDef) return;

			// If originalRoleDef hasn't been set yet, set it now
			if (!this.originalRoleDef) {
				this.originalRoleDef = _cloneDeep(this.editingRoleDef);
			}

			this.isEditMode = true;
		},
		exitEditMode () {
			this.isEditMode = false;
			this.editingRoleDef = _cloneDeep(this.originalRoleDef);
		},
		async saveAsNewRole () {
			// TODO: this is currently RunDiffusion admins only
			if (!this.user.isAdmin) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'You do not have permission to create new roles',
					show: true,
				});
				return;
			}
			if (!this.editingRoleDef) return;

			this.saving = true;
			try {
				const companyId: string = this.team.companyId;
				if (!companyId) {
					console.error('No company ID found for user');
					return;
				}

				// Filter out permissions with false values, keeping only true permissions
				const filteredPermissions = Object.entries(this.editingRoleDef.permissions)
					.filter(([_, value]) => value === true)
					.reduce((acc, [key, value]) => {
						acc[key] = true;
						return acc;
					}, {} as Record<string, true>);

				// Call the Firebase function to create a new role definition
				const createCompanyUserRoleDefFunction = functions.httpsCallable('createCompanyUserRoleDef');
				const result = await createCompanyUserRoleDefFunction({
					roleDef: {
						companyId: companyId,
						name: this.editingRoleDef.name || 'New Role',
						description: this.editingRoleDef.description || '',
						color: this.editingRoleDef.color || '#1e88e5',
						permissions: filteredPermissions,
						...(this.editingRoleDef.required && { required: true }),
					},
				});

				if (result.data.success) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: result.data.message || `Successfully created new role: ${this.editingRoleDef.name}`,
						show: true,
					});
					this.exitEditMode();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: result.data.message || 'Failed to create new role',
						show: true,
					});
				}
			} catch (error) {
				console.error('Error creating new role:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to create new role',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		EmptyState,
		BackButton,
		RoleDefBadge,
		BaseBreadcrumbs,
		BaseButton,
		ColorInput,
		BaseToggle,
	},
});
